/**
 * RetirementInputPanel Component
 * WP-RETIRE-2, WP-RETIRE-5: Input form with debounced saves
 *
 * Supports two variants:
 * - 'dashboard': Simplified view (combined retirement income, no start ages)
 * - 'full': Split Working Phase / Retirement Phase with per-source start ages
 *
 * Age inputs removed - currentAge derived from birth year, retirementAge set via slider.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import Input from '@/components/ui/Input';
import { dollarsToCents, centsToDollars } from '@/utils/format';
import {
  MIN_SOCIAL_SECURITY_START_AGE,
  clampSocialSecurityStartAge,
  effectiveSocialSecurityStartAge,
} from '@/utils/retirementCalculations';
import type { RetirementPlanningInputs, RetirementInputsUpdate } from '@/types/retirement';

interface RetirementInputPanelProps {
  inputs: RetirementPlanningInputs;
  isAlreadyRetired: boolean;
  /** Called with FULL merged payload - backend requires all fields */
  onUpdate: (values: RetirementInputsUpdate) => void;
  /** 'dashboard' = simplified, 'full' = split Working/Retirement sections */
  variant?: 'dashboard' | 'full';
}

// ============================================================================
// Validation Functions (spec Section 4.3)
// ============================================================================

function validateField(
  field: string,
  value: number,
): string {
  switch (field) {
    // Net worth adjustment: no limits (can be negative for debts not tracked)
    case 'netWorthAdjustmentCents':
      return '';

    // Retirement income fields: $0 - $200K/500K
    case 'socialSecurityAnnualCents':
      if (value < 0) return 'Social Security cannot be negative';
      if (value > 20_000_000) return 'Social Security cannot exceed $200,000/year';
      return '';
    case 'pensionAnnualCents':
      if (value < 0) return 'Pension cannot be negative';
      if (value > 50_000_000) return 'Pension cannot exceed $500,000/year';
      return '';
    case 'otherRetirementIncomeCents':
      if (value < 0) return 'Other income cannot be negative';
      if (value > 50_000_000) return 'Other income cannot exceed $500,000/year';
      return '';

    // Required expense field: $10K - $1M
    case 'retirementExpensesAnnualCents':
      if (value < 1_000_000) return 'Expenses must be at least $10,000/year';
      if (value > 100_000_000) return 'Expenses cannot exceed $1,000,000/year';
      return '';

    // Current savings: can be negative (expenses > income)
    case 'currentSavingsAnnualCents':
      if (value > 50_000_000) return 'Savings cannot exceed $500,000/year';
      return '';

    // Monthly income (WP-RETIRE-5): virtual field
    case 'monthlyIncome':
      if (value < 0) return 'Income cannot be negative';
      return '';

    // Monthly expenses (WP-RETIRE-5): virtual field
    case 'monthlyExpenses':
      if (value < 0) return 'Expenses cannot be negative';
      return '';

    // Start age fields. Social Security has its own floor: the SSA pays no
    // retirement benefit before 62 (MIN_SOCIAL_SECURITY_START_AGE).
    case 'socialSecurityStartAge':
      if (value < MIN_SOCIAL_SECURITY_START_AGE) {
        return `Social Security can't start before age ${MIN_SOCIAL_SECURITY_START_AGE}`;
      }
      if (value > 90) return 'Start age must be between 62 and 90';
      return '';
    case 'pensionStartAge':
    case 'otherIncomeStartAge':
      if (value < 50 || value > 90) return 'Start age must be between 50 and 90';
      return '';

    // Retirement expenses override (monthly, full variant)
    case 'retirementExpensesMonthly':
      if (value < 0) return 'Expenses cannot be negative';
      return '';

    default:
      return '';
  }
}

/**
 * WP-PLAN-2: show a saved Social Security start age below 62 as 62 - the age
 * the projection really uses - so the form never displays an age the plan
 * ignores. Applied only when syncing from saved inputs, never while typing, so
 * a partial entry such as "6" is shown as typed and flagged rather than
 * snapping to 62 under the cursor.
 */
function withSocialSecurityFloor(
  inputs: RetirementPlanningInputs
): RetirementPlanningInputs {
  return {
    ...inputs,
    socialSecurityStartAge: clampSocialSecurityStartAge(inputs.socialSecurityStartAge),
  };
}

// ============================================================================
// Main Component
// ============================================================================

export default function RetirementInputPanel({
  inputs,
  isAlreadyRetired,
  onUpdate,
  variant = 'dashboard',
}: RetirementInputPanelProps) {
  const [localValues, setLocalValues] = useState(() => withSocialSecurityFloor(inputs));
  const [errors, setErrors] = useState<Record<string, string>>({});

  // WP-RETIRE-9: Reconstruct monthly income from workingIncomeAnnualCents if available,
  // otherwise fall back to savings + retirementExpenses (old behavior)
  const [monthlyIncomeDollars, setMonthlyIncomeDollars] = useState(() => {
    if (inputs.workingIncomeAnnualCents != null) {
      return centsToDollars(inputs.workingIncomeAnnualCents) / 12;
    }
    const annualIncomeCents =
      inputs.currentSavingsAnnualCents + inputs.retirementExpensesAnnualCents;
    return centsToDollars(annualIncomeCents) / 12;
  });

  const [monthlyExpensesDollars, setMonthlyExpensesDollars] = useState(() => {
    if (inputs.workingIncomeAnnualCents != null) {
      const derivedExpenses = inputs.workingIncomeAnnualCents - inputs.currentSavingsAnnualCents;
      return centsToDollars(derivedExpenses) / 12;
    }
    return centsToDollars(inputs.retirementExpensesAnnualCents) / 12;
  });

  // Retirement expenses override for full variant (defaults to working expenses)
  const [retirementExpensesMonthly, setRetirementExpensesMonthly] = useState(() => {
    return centsToDollars(inputs.retirementExpensesAnnualCents) / 12;
  });

  // Derived monthly savings
  const monthlySavingsDollars = useMemo(() => {
    return monthlyIncomeDollars - monthlyExpensesDollars;
  }, [monthlyIncomeDollars, monthlyExpensesDollars]);

  // Sync with props when external data changes
  useEffect(() => {
    setLocalValues(withSocialSecurityFloor(inputs));
    // WP-RETIRE-9: If workingIncomeAnnualCents is available, use it directly
    if (inputs.workingIncomeAnnualCents != null) {
      setMonthlyIncomeDollars(centsToDollars(inputs.workingIncomeAnnualCents) / 12);
      // Derive expenses from income - savings
      const derivedExpenses = inputs.workingIncomeAnnualCents - inputs.currentSavingsAnnualCents;
      setMonthlyExpensesDollars(centsToDollars(derivedExpenses) / 12);
    } else {
      // Fallback: derive income from savings + retirementExpenses (old behavior)
      const annualIncomeCents =
        inputs.currentSavingsAnnualCents + inputs.retirementExpensesAnnualCents;
      setMonthlyIncomeDollars(centsToDollars(annualIncomeCents) / 12);
      setMonthlyExpensesDollars(centsToDollars(inputs.retirementExpensesAnnualCents) / 12);
    }
    setRetirementExpensesMonthly(centsToDollars(inputs.retirementExpensesAnnualCents) / 12);
  }, [inputs]);

  /**
   * Convert RetirementPlanningInputs (response) to RetirementInputsUpdate (request).
   * Strips response-only fields (version, updatedAt) to create valid request payload.
   */
  const toUpdatePayload = useCallback(
    (values: RetirementPlanningInputs): RetirementInputsUpdate => ({
      currentAge: values.currentAge,
      retirementAge: values.retirementAge,
      netWorthAdjustmentCents: values.netWorthAdjustmentCents,
      socialSecurityAnnualCents: values.socialSecurityAnnualCents,
      pensionAnnualCents: values.pensionAnnualCents,
      otherRetirementIncomeCents: values.otherRetirementIncomeCents,
      retirementExpensesAnnualCents: values.retirementExpensesAnnualCents,
      currentSavingsAnnualCents: values.currentSavingsAnnualCents,
      socialSecurityStartAge: values.socialSecurityStartAge,
      pensionStartAge: values.pensionStartAge,
      otherIncomeStartAge: values.otherIncomeStartAge,
      workingIncomeAnnualCents: values.workingIncomeAnnualCents,
    }),
    []
  );

  // Debounced save - 500ms after last change
  const debouncedSave = useDebouncedCallback(
    useCallback(
      (mergedValues: RetirementPlanningInputs, changedField: string) => {
        const error = validateField(changedField, mergedValues[changedField as keyof RetirementPlanningInputs] as number);
        if (!error) {
          onUpdate(toUpdatePayload(mergedValues));
        }
      },
      [onUpdate, toUpdatePayload]
    ),
    500
  );

  const handleFieldChange = (field: keyof RetirementInputsUpdate, value: number) => {
    const newValues = { ...localValues, [field]: value } as RetirementPlanningInputs;
    setLocalValues(newValues);

    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));

    debouncedSave(newValues, field);
  };

  // Handle currency fields (display in dollars, store in cents)
  const handleCurrencyChange = (field: keyof RetirementInputsUpdate, dollarValue: string) => {
    const dollars = parseFloat(dollarValue) || 0;
    const cents = dollarsToCents(dollars);
    handleFieldChange(field, cents);
  };

  // Handle start age fields
  const handleStartAgeChange = (
    field: 'socialSecurityStartAge' | 'pensionStartAge' | 'otherIncomeStartAge',
    value: string
  ) => {
    const age = parseInt(value, 10) || 0;
    handleFieldChange(field, age);
  };

  /**
   * WP-RETIRE-5: Handle monthly income/expenses changes.
   * Derives retirementExpensesAnnualCents and currentSavingsAnnualCents
   * from monthly values.
   */
  const handleMonthlyIncomeChange = (dollarValue: string) => {
    const dollars = parseFloat(dollarValue) || 0;
    setMonthlyIncomeDollars(dollars);

    const error = validateField('monthlyIncome', dollars);
    setErrors((prev) => ({ ...prev, monthlyIncome: error }));

    // Derive backend fields
    const newSavings = dollars - monthlyExpensesDollars;
    const annualSavingsCents = dollarsToCents(newSavings * 12);

    // WP-RETIRE-9: Working expenses are independent from retirement expenses
    const newValues = {
      ...localValues,
      currentSavingsAnnualCents: annualSavingsCents,
      workingIncomeAnnualCents: dollarsToCents(dollars * 12),
    } as RetirementPlanningInputs;

    setLocalValues(newValues);
    debouncedSave(newValues, 'currentSavingsAnnualCents');
  };

  const handleMonthlyExpensesChange = (dollarValue: string) => {
    const dollars = parseFloat(dollarValue) || 0;
    setMonthlyExpensesDollars(dollars);

    const error = validateField('monthlyExpenses', dollars);
    setErrors((prev) => ({ ...prev, monthlyExpenses: error }));

    const newSavings = monthlyIncomeDollars - dollars;
    const annualSavingsCents = dollarsToCents(newSavings * 12);

    // WP-RETIRE-9: Working expenses are independent from retirement expenses
    const newValues = { ...localValues } as RetirementPlanningInputs;
    newValues.currentSavingsAnnualCents = annualSavingsCents;
    // Compute working income from current monthly income
    const workingIncomeAnnual = dollarsToCents(monthlyIncomeDollars * 12);
    newValues.workingIncomeAnnualCents = workingIncomeAnnual;

    setLocalValues(newValues);
    debouncedSave(newValues, 'currentSavingsAnnualCents');
  };

  /** Handle retirement expenses independently in full variant */
  const handleRetirementExpensesMonthlyChange = (dollarValue: string) => {
    const dollars = parseFloat(dollarValue) || 0;
    setRetirementExpensesMonthly(dollars);

    const error = validateField('retirementExpensesMonthly', dollars);
    setErrors((prev) => ({ ...prev, retirementExpensesMonthly: error }));

    const retirementExpensesCents = dollarsToCents(dollars * 12);
    const newValues = {
      ...localValues,
      retirementExpensesAnnualCents: retirementExpensesCents,
    } as RetirementPlanningInputs;
    setLocalValues(newValues);
    debouncedSave(newValues, 'retirementExpensesAnnualCents');
  };

  // ---- Full variant: Working Phase + Retirement Phase ----
  if (variant === 'full') {
    // Total retirement income for display
    const totalRetirementIncomeDollars = centsToDollars(
      localValues.socialSecurityAnnualCents +
      localValues.pensionAnnualCents +
      localValues.otherRetirementIncomeCents
    );

    return (
      <div className="space-y-8">
        {/* Net Worth Adjustment — modifies today's calculated net worth */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
            Net Worth
          </h3>
          <Input
            label="Adjustment"
            type="number"
            value={centsToDollars(localValues.netWorthAdjustmentCents)}
            onChange={(e) => handleCurrencyChange('netWorthAdjustmentCents', e.target.value)}
            error={errors.netWorthAdjustmentCents}
            hint="Adjust today's net worth (calculated from your accounts) for assets or debts not tracked in FlowIO"
          />
        </div>

        {/* Working Phase */}
        {!isAlreadyRetired && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Working Phase
            </h3>
            <div className="space-y-4">
              <Input
                label="Monthly Income"
                type="number"
                min={0}
                value={Math.round(monthlyIncomeDollars)}
                onChange={(e) => handleMonthlyIncomeChange(e.target.value)}
                error={errors.monthlyIncome}
                hint={`$${Math.round(monthlyIncomeDollars * 12).toLocaleString()}/yr`}
              />
              <Input
                label="Monthly Expenses"
                type="number"
                min={0}
                value={Math.round(monthlyExpensesDollars)}
                onChange={(e) => handleMonthlyExpensesChange(e.target.value)}
                error={errors.monthlyExpenses}
                hint={`$${Math.round(monthlyExpensesDollars * 12).toLocaleString()}/yr`}
              />
              {/* Monthly Savings (Derived, non-editable) */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Savings
                </label>
                <input
                  type="text"
                  readOnly
                  value={`$${Math.round(monthlySavingsDollars).toLocaleString()}`}
                  className={`block w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm bg-gray-50 cursor-not-allowed ${
                    monthlySavingsDollars < 0 ? 'text-red-600' : 'text-gray-700'
                  }`}
                  tabIndex={-1}
                />
                <p className={`mt-1 text-sm ${monthlySavingsDollars < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                  ${Math.round(monthlySavingsDollars * 12).toLocaleString()}/yr = Income - Expenses
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Retirement Phase */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
            Retirement Phase
          </h3>
          <div className="space-y-4">
            <Input
              label="Monthly Retirement Expenses"
              type="number"
              min={0}
              value={Math.round(retirementExpensesMonthly)}
              onChange={(e) => handleRetirementExpensesMonthlyChange(e.target.value)}
              error={errors.retirementExpensesMonthly}
              hint={
                !isAlreadyRetired
                  ? `$${Math.round(retirementExpensesMonthly * 12).toLocaleString()}/yr (defaults to working expenses)`
                  : `$${Math.round(retirementExpensesMonthly * 12).toLocaleString()}/yr`
              }
            />

            {/* Social Security with start age */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Input
                  label="Social Security (annual)"
                  type="number"
                  min={0}
                  value={centsToDollars(localValues.socialSecurityAnnualCents)}
                  onChange={(e) => handleCurrencyChange('socialSecurityAnnualCents', e.target.value)}
                  error={errors.socialSecurityAnnualCents}
                />
              </div>
              {/* An unset age shows when Social Security really starts in the
                  projection: at retirement age, but never below 62.
                  Explicit ids on all three start ages: Input derives its id
                  from the label, and all three are labelled "Start Age", so
                  without them every label pointed at this input. */}
              <Input
                id="social-security-start-age"
                label="Start Age"
                type="number"
                min={MIN_SOCIAL_SECURITY_START_AGE}
                max={90}
                value={
                  localValues.socialSecurityStartAge ??
                  effectiveSocialSecurityStartAge(null, localValues.retirementAge)
                }
                onChange={(e) => handleStartAgeChange('socialSecurityStartAge', e.target.value)}
                error={errors.socialSecurityStartAge}
                hint={`${MIN_SOCIAL_SECURITY_START_AGE} or later`}
              />
            </div>

            {/* Pension with start age */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Input
                  label="Pension (annual)"
                  type="number"
                  min={0}
                  value={centsToDollars(localValues.pensionAnnualCents)}
                  onChange={(e) => handleCurrencyChange('pensionAnnualCents', e.target.value)}
                  error={errors.pensionAnnualCents}
                />
              </div>
              <Input
                label="Start Age"
                type="number"
                min={50}
                max={90}
                id="pension-start-age"
                value={localValues.pensionStartAge ?? localValues.retirementAge}
                onChange={(e) => handleStartAgeChange('pensionStartAge', e.target.value)}
                error={errors.pensionStartAge}
              />
            </div>

            {/* Other Retirement Income with start age */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Input
                  label="Other Income (annual)"
                  type="number"
                  min={0}
                  value={centsToDollars(localValues.otherRetirementIncomeCents)}
                  onChange={(e) => handleCurrencyChange('otherRetirementIncomeCents', e.target.value)}
                  error={errors.otherRetirementIncomeCents}
                />
              </div>
              <Input
                label="Start Age"
                type="number"
                min={50}
                max={90}
                id="other-income-start-age"
                value={localValues.otherIncomeStartAge ?? localValues.retirementAge}
                onChange={(e) => handleStartAgeChange('otherIncomeStartAge', e.target.value)}
                error={errors.otherIncomeStartAge}
              />
            </div>

            {/* Total retirement income (read-only summary) */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Retirement Income
              </label>
              <input
                type="text"
                readOnly
                value={`$${Math.round(totalRetirementIncomeDollars).toLocaleString()}/yr`}
                className="block w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm bg-gray-50 cursor-not-allowed text-gray-700"
                tabIndex={-1}
              />
              <p className="mt-1 text-sm text-gray-500">
                Sum of Social Security + Pension + Other
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Dashboard variant: Simplified flat layout ----
  return (
    <div className="space-y-6">
      <Input
        label="Net Worth Adjustment"
        type="number"
        value={centsToDollars(localValues.netWorthAdjustmentCents)}
        onChange={(e) => handleCurrencyChange('netWorthAdjustmentCents', e.target.value)}
        error={errors.netWorthAdjustmentCents}
        hint="Add/subtract assets not tracked in FlowIO (can be negative)"
      />

      {/* Total retirement income (read-only, sum of all sources) */}
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Retirement Income (annual)
        </label>
        <input
          type="text"
          readOnly
          value={`$${Math.round(centsToDollars(
            localValues.socialSecurityAnnualCents +
            localValues.pensionAnnualCents +
            localValues.otherRetirementIncomeCents
          )).toLocaleString()}`}
          className="block w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm bg-gray-50 cursor-not-allowed text-gray-700"
          tabIndex={-1}
        />
        <p className="mt-1 text-sm text-gray-500">
          Edit per-source amounts on the{' '}
          <a href="/planning" className="text-primary-600 hover:text-primary-700 underline">
            Retirement page
          </a>
        </p>
      </div>

      {/* WP-RETIRE-5 Change 3: Income / Expenses / Derived Savings */}
      {!isAlreadyRetired && (
        <>
          <div>
            <Input
              label="Monthly Income"
              type="number"
              min={0}
              value={Math.round(monthlyIncomeDollars)}
              onChange={(e) => handleMonthlyIncomeChange(e.target.value)}
              error={errors.monthlyIncome}
              hint={`$${Math.round(monthlyIncomeDollars * 12).toLocaleString()}/yr`}
            />
          </div>

          <div>
            <Input
              label="Monthly Expenses"
              type="number"
              min={0}
              value={Math.round(monthlyExpensesDollars)}
              onChange={(e) => handleMonthlyExpensesChange(e.target.value)}
              error={errors.monthlyExpenses}
              hint={`$${Math.round(monthlyExpensesDollars * 12).toLocaleString()}/yr`}
            />
          </div>

          {/* Monthly Savings (Derived, non-editable) */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Savings
            </label>
            <input
              type="text"
              readOnly
              value={`$${Math.round(monthlySavingsDollars).toLocaleString()}`}
              className={`block w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm bg-gray-50 cursor-not-allowed ${
                monthlySavingsDollars < 0 ? 'text-red-600' : 'text-gray-700'
              }`}
              tabIndex={-1}
            />
            <p className={`mt-1 text-sm ${monthlySavingsDollars < 0 ? 'text-red-500' : 'text-gray-500'}`}>
              ${Math.round(monthlySavingsDollars * 12).toLocaleString()}/yr = Income - Expenses
            </p>
          </div>
        </>
      )}

      {/* WP-RETIRE-9: Retirement expenses (decoupled from working expenses) */}
      <Input
        label="Monthly Retirement Expenses"
        type="number"
        min={0}
        value={Math.round(retirementExpensesMonthly)}
        onChange={(e) => handleRetirementExpensesMonthlyChange(e.target.value)}
        error={errors.retirementExpensesMonthly}
        hint={`$${Math.round(retirementExpensesMonthly * 12).toLocaleString()}/yr`}
      />
    </div>
  );
}
